import React, { useState } from 'react';
import { HomeIcon, UsersIcon, SettingsIcon, SearchIcon, FileTextIcon, ListIcon, ChevronRightIcon, ChevronDownIcon, ActivityIcon, ClipboardListIcon, UserIcon, FileIcon, CreditCardIcon, BarChartIcon, ShieldIcon, LayersIcon, ArrowDownIcon, ArrowUpIcon, KeyIcon } from 'lucide-react';
interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onNavigate: (path: string) => void;
  activePath: string;
}
interface MenuItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  submenu?: MenuItem[];
}
export function Sidebar({
  collapsed,
  onToggleCollapse,
  onNavigate,
  activePath
}: SidebarProps) {
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['home']);
  const menuItems: MenuItem[] = [{
    title: 'Dashboard',
    path: 'dashboard',
    icon: <HomeIcon size={20} data-id="element-188" />
  }, {
    title: 'User Management',
    path: 'management',
    icon: <UsersIcon size={20} data-id="element-189" />,
    submenu: [{
      title: 'Role Management',
      path: 'management/roles',
      icon: <UserIcon size={20} data-id="element-190" />
    }, {
      title: 'User Management',
      path: 'management/users',
      icon: <UsersIcon size={20} data-id="element-191" />
    }]
  }, {
    title: 'Transaction Services',
    path: 'transactions',
    icon: <CreditCardIcon size={20} data-id="element-192" />,
    submenu: [{
      title: 'Transaction Monitoring',
      path: 'transactions/monitoring',
      icon: <ActivityIcon size={20} data-id="element-193" />
    }, {
      title: 'Transaction Search',
      path: 'transactions/search',
      icon: <SearchIcon size={20} data-id="element-194" />
    }]
  }, {
    title: 'Batch Payments',
    path: 'batch',
    icon: <LayersIcon size={20} data-id="element-195" />,
    submenu: [{
      title: 'Bulk Sending',
      path: 'batch/payments',
      icon: <ArrowUpIcon size={20} data-id="element-196" />
    }, {
      title: 'Bulk Receiving',
      path: 'batch/collections',
      icon: <ArrowDownIcon size={20} data-id="element-197" />
    }]
  }, {
    title: 'Reports & Analytics',
    path: 'reports',
    icon: <BarChartIcon size={20} data-id="element-198" />,
    submenu: [{
      title: 'Transaction Report',
      path: 'reports/transactions',
      icon: <FileIcon size={20} data-id="element-199" />
    }, {
      title: 'Transaction Summary',
      path: 'reports/summary',
      icon: <ClipboardListIcon size={20} data-id="element-200" />
    }, {
      title: 'Users List',
      path: 'reports/users',
      icon: <ListIcon size={20} data-id="element-201" />
    }, {
      title: 'User Activity Audit',
      path: 'reports/user-activity',
      icon: <ActivityIcon size={20} data-id="element-202" />
    }]
  }, {
    title: 'Security & Compliance',
    path: 'security',
    icon: <ShieldIcon size={20} data-id="element-203" />,
    submenu: [{
      title: 'Audit Log',
      path: 'audit-log',
      icon: <ListIcon size={20} data-id="element-204" />
    }, {
      title: 'Certificate Management',
      path: 'security/certificates',
      icon: <KeyIcon size={20} data-id="element-205" />
    }]
  }, {
    title: 'Configurations',
    path: 'configurations',
    icon: <SettingsIcon size={20} data-id="element-206" />
  }];
  const toggleMenu = (path: string) => {
    setExpandedMenus(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  };
  const handleMenuItemClick = (path: string) => {
    onNavigate(path);
  };
  const renderMenuItem = (item: MenuItem, level = 0) => {
    const isActive = activePath === item.path;
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isExpanded = expandedMenus.includes(item.path);
    // Check if any submenu item is active
    const isSubmenuActive = hasSubmenu && item.submenu!.some(subItem => activePath === subItem.path);
    return <div key={item.path} data-id="element-207">
        <button onClick={() => hasSubmenu ? toggleMenu(item.path) : handleMenuItemClick(item.path)} className={`flex items-center w-full text-left px-4 py-2 rounded-md transition-colors ${isActive || isSubmenuActive ? 'bg-[#F58220] bg-opacity-10 text-[#F58220]' : 'text-gray-700 hover:bg-gray-100'}`} style={{
        paddingLeft: `${level * 12 + 16}px`
      }} data-id="element-208">
          <span className="mr-3" data-id="element-209">{item.icon}</span>
          {!collapsed && <>
              <span className="flex-grow" data-id="element-210">{item.title}</span>
              {hasSubmenu && (isExpanded ? <ChevronDownIcon size={16} data-id="element-211" /> : <ChevronRightIcon size={16} data-id="element-212" />)}
            </>}
        </button>
        {hasSubmenu && isExpanded && !collapsed && <div className="mt-1" data-id="element-213">
            {item.submenu!.map(subItem => renderMenuItem(subItem, level + 1))}
          </div>}
      </div>;
  };
  return <aside className={`bg-white border-r border-gray-200 h-full transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'}`} data-id="element-214">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-215">
        {!collapsed && <h2 className="font-semibold text-[#1A1F36]" data-id="element-216">Navigation</h2>}
        <button onClick={onToggleCollapse} className="p-1 rounded-md hover:bg-gray-100" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-id="element-217">
          {collapsed ? <ChevronRightIcon size={20} data-id="element-218" /> : <ChevronDownIcon size={20} data-id="element-219" />}
        </button>
      </div>
      <nav className="p-2 space-y-1 overflow-y-auto max-h-[calc(100vh-150px)]" data-id="element-220">
        {menuItems.map(item => renderMenuItem(item))}
      </nav>
    </aside>;
}